// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contador/ContadorDefinirSenha.tsx
//  Link do convite (o cliente liberou o acesso) ou do "esqueci minha senha" do
//  Portal do Contador: consome o token de uso único e grava a senha.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { getApiErrorMessage } from '@/lib/apiClient';
import { contadorService } from '@/features/contador/contadorService';
import logo from '@/assets/logo.jpg';

const SENHA_MINIMA = 8;

export default function ContadorDefinirSenha() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [senha, setSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [carregando, setCarregando] = useState(false);

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (senha.length < SENHA_MINIMA) return toast.error(`A senha precisa ter ao menos ${SENHA_MINIMA} caracteres.`);
    if (senha !== confirmar) return toast.error('As senhas não coincidem.');
    setCarregando(true);
    try {
      await contadorService.definirSenha(token, senha);
      toast.success('Senha definida', { description: 'Agora entre com seu e-mail e a nova senha.' });
      navigate('/contador/entrar');
    } catch (erro) {
      toast.error('Não foi possível definir a senha', { description: getApiErrorMessage(erro) });
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex justify-center">
          <img src={logo} alt="Udisys" className="h-14 w-auto object-contain" />
        </div>
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Criar senha</CardTitle>
            <CardDescription className="text-center">Senha de acesso ao Portal do Contador</CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Link inválido. Abra o link mais recente recebido por e-mail ou peça um novo em "Esqueci minha senha".
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/contador/entrar">Ir para a entrada do portal</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={salvar} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="senha">Nova senha</Label>
                  <Input id="senha" type="password" autoComplete="new-password" required disabled={carregando}
                    value={senha} onChange={(e) => setSenha(e.target.value)} />
                  <PasswordStrengthIndicator password={senha} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmar">Confirmar senha</Label>
                  <Input id="confirmar" type="password" autoComplete="new-password" required disabled={carregando}
                    value={confirmar} onChange={(e) => setConfirmar(e.target.value)} />
                </div>
                <Button type="submit" className="w-full" disabled={carregando}>
                  {carregando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                  Salvar senha
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
